// src/components/core/entity/EntityChannelsCard/ContactsChannelModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo } from 'react';
import { PhoneOutlined } from '@ant-design/icons';
import GenericModal from 'components/core/GenericModal';
import appService from 'services/appService';
import entitiesService from 'services/entitiesService';
import whatsappService from 'services/whatsappService';
import contactChannelStep from './Step1';
import { returnOnlyNumbers, generateMongoObjectId } from 'utils/helpers';

// *******************************************************************************
// HELPERS
// *******************************************************************************
/**
 * Determine channelType from contactType
 */
function getChannelTypeFromContactType(contactType) {
  if (!contactType) return 'phone'; // Default to phone (first in segment order)

  if (contactType === 'website') return 'website';
  if (contactType === 'email') return 'email';
  if (contactType === 'phone') return 'phone';
  if (['facebook', 'linkedin', 'instagram', 'twitter', 'youtube', 'tiktok'].includes(contactType)) {
    return 'social';
  }
  return 'other';
}

/**
 * Extract phone value from various formats
 */
function extractPhoneValue(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    return value.value || value.phone || '';
  }
  return '';
}

/**
 * Extract phone metadata from value object
 */
function extractPhoneMetadata(value) {
  if (!value || typeof value !== 'object') {
    return { countryCode: 'BR', phoneType: null };
  }
  return {
    countryCode: value.countryCode || 'BR',
    phoneType: value.type || value.phoneType || null,
  };
}

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ContactChannelModal = ({
  show,
  setShow,
  afterClose,
  initialData,
  entityId,
  onSuccess,
}) => {
  // 1. LOCAL VARIABLES
  const isEditing = !!initialData?._id;
  const title = isEditing ? 'edit_channel' : 'new_channel';

  // 2. INITIAL VALUES
  const initialValues = useMemo(() => {
    const channelType = getChannelTypeFromContactType(initialData?.contactType);

    // For social networks, extract the specific network
    let socialNetwork = null;
    if (channelType === 'social' && initialData?.contactType) {
      socialNetwork = initialData.contactType;
    }

    // All fields use { value: ... } object structure since they use name={['value', 'value']}
    // For phone, include additional properties (countryCode, type)
    let value;
    if (channelType === 'phone') {
      value = {
        value: initialData?.value || '',
        countryCode: initialData?.countryCode || 'BR',
        type: initialData?.phoneType || '',
      };
    } else {
      // For website, email, social, other - just wrap the value
      value = {
        value: initialData?.value || '',
      };
    }

    return {
      _contactId: initialData?._id, // Store original contact ID for editing
      channelType,
      socialNetwork,
      value,
      note: initialData?.note || '',
      isPrimary: initialData?.isPrimary || false,
    };
  }, [initialData]);

  // 3. STEPS
  const steps = useMemo(() => [contactChannelStep()], []);

  // 4. HANDLERS
  const handleFinish = async ({ form, goFocus }) => {
    try {
      const values = form.getFieldsValue();

      // Determine contactType from channelType
      let contactType = values.channelType;
      if (values.channelType === 'social' && values.socialNetwork) {
        contactType = values.socialNetwork;
      }

      // Extract phone value and metadata
      const rawValue = values.value;
      let contactValue = extractPhoneValue(rawValue);
      const phoneMetadata = extractPhoneMetadata(rawValue);

      // Trim string values
      if (typeof contactValue === 'string') {
        contactValue = contactValue.trim();
      }

      // Validate value is not empty
      if (!contactValue) {
        await appService.message({ flag: 'w', text: 'field_required' });
        // All fields use value.value structure
        goFocus?.('value.value', true);
        return false;
      }

      // Build contact data for API
      const contactData = {
        contactType,
        value: contactValue,
        note: (values.note || '').trim(),
        isPrimary: values.isPrimary || false,
        countryCode: phoneMetadata.countryCode,
        phoneType: phoneMetadata.phoneType,
      };

      // If editing, include the existing _id; if creating new, generate one
      if (values._contactId) {
        contactData._id = values._contactId;
      } else {
        // Generate _id on frontend for new contacts
        // This ensures we have the correct _id for subsequent edit/delete operations
        contactData._id = generateMongoObjectId();
      }

      // Check WhatsApp for phone contacts
      if (contactType === 'phone' && contactValue) {
        // Only check if creating new or phone number changed
        // Normalize both values to only numbers to ensure proper comparison
        const normalizedInitialValue = returnOnlyNumbers(initialData?.value);
        const normalizedCurrentValue = returnOnlyNumbers(contactValue);
        const phoneChanged = !isEditing || normalizedInitialValue !== normalizedCurrentValue;

        if (phoneChanged) {
          try {
            const hasWhatsApp = await whatsappService.hasWhatsApp(contactValue, phoneMetadata.countryCode);
            // Only update if we got a valid boolean response
            if (typeof hasWhatsApp === 'boolean') {
              contactData.hasWhatsApp = hasWhatsApp;
            } else {
              // Invalid response, keep existing or default to false
              contactData.hasWhatsApp = isEditing ? (initialData?.hasWhatsApp || false) : false;
            }
          } catch (error) {
            // WhatsApp check failed, keep existing value or default to false
            contactData.hasWhatsApp = isEditing ? (initialData?.hasWhatsApp || false) : false;
          }
        } else {
          // Keep existing WhatsApp status
          contactData.hasWhatsApp = initialData?.hasWhatsApp || false;
        }
      }

      // Save via add-to-set API
      await appService.message({ flag: 'i', text: 'saving_data', key: 'saveContact' });

      // NOTE: The add-to-set API may return null even on success because the backend
      // returns an empty array [] which gets processed as null by axiosService.done().
      // We catch exceptions for actual errors, and treat null response as success.
      const updatedEntity = await entitiesService.addToSet({
        _id: entityId,
        operator: '$push',
        path: 'contacts',
        data: contactData,
      });

      // If we get here without exception, the API call succeeded
      // The backend saved the data even if updatedEntity is null (empty array response)
      await appService.message({
        flag: 's',
        text: isEditing ? 'channel_updated' : 'channel_added',
        key: 'saveContact',
      });

      // Call success callback - pass contactData since updatedEntity might be null
      if (onSuccess) {
        await onSuccess(updatedEntity, contactData);
      }

      return true; // close modal
    } catch (error) {
      await appService.message({
        flag: 'e',
        text: 'error_saving',
        key: 'saveContact',
      });
      return false; // keep modal open
    }
  };

  // 5. UI
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      afterClose={afterClose}
      icon={<PhoneOutlined />}
      title={title}
      steps={steps}
      initialValues={initialValues}
      onFinish={handleFinish}
      width={500}
      maskClosable={false}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ContactChannelModal;
